/**
 * auth.service.ts — Firebase Auth wrapper
 *
 * 컴포넌트는 이 모듈만 본다. Firebase User / Claims는 도메인 User로 변환되어 노출된다.
 *
 * 가상 이메일 규칙: {loginId}@rmc-internal.local
 * 권한 캐시: tenants/{t}/units/{biz}/roles/{roleId}.permissions 60s TTL (기준 사업부 roleUnitOf)
 */
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  type User as FbUser,
} from "firebase/auth";
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db, INTERNAL_EMAIL_DOMAIN } from "./firebase";
import type { AuthClaims, Permission, PermOverrides, User } from "@/domain/user";
import { JOINT_USE_CONSENT_VERSION, PRIVACY_NOTICE_VERSION, TERMS_CONSENT_VERSION } from "@/domain/consent";
import { defaultBiz, isTenantKey, type BizKey, type TenantKey } from "@/domain/space";
import { ROOT_ROLE_UNIT } from "./roles.repository";
import { writeSelfAccessLog, type SelfAccessLogInput } from "./access_log.repository";

export type AuthErrorCode =
  | "INVALID_CREDENTIALS"
  | "TOO_MANY_ATTEMPTS"
  | "ACCOUNT_DISABLED"
  | "PENDING_APPROVAL"
  | "ID_TAKEN"
  | "WEAK_PASSWORD"
  | "WRONG_CURRENT_PASSWORD"
  | "REQUIRES_RECENT_LOGIN"
  | "UNKNOWN";

export class AuthError extends Error {
  readonly code: AuthErrorCode;
  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code);
    this.name = "AuthError";
    this.code = code;
  }
}

const PERMS_TTL_MS = 60_000;
type RoleAccess = { perms: Permission[]; allMenus: boolean };
const permsCache = new Map<string, { access: RoleAccess; at: number }>();

/**
 * 계정 부서(claims role)의 권한 — 사업부별 부서 문서(12 §16-2). 기준 사업부 = 중앙 소속(또는 소속 없음)이면 중앙 레미콘
 * (rules 루트 판정과 같음), 아니면 첫 근무 법인의 기본 사업부(계정을 만든 법인의 부서가 claims role).
 */
export function roleUnitOf(tenants: readonly TenantKey[]): { tenant: TenantKey; biz: BizKey } {
  if (!tenants.length || tenants.includes(ROOT_ROLE_UNIT.tenant)) return ROOT_ROLE_UNIT;
  return { tenant: tenants[0], biz: defaultBiz(tenants[0]) };
}

async function loadRoleAccess(role: string | null, tenants: readonly TenantKey[]): Promise<RoleAccess> {
  if (!role) return { perms: [], allMenus: false };
  const unit = roleUnitOf(tenants);
  const cacheKey = `${unit.tenant}/${unit.biz}/${role}`;
  const hit = permsCache.get(cacheKey);
  const now = Date.now();
  if (hit && now - hit.at < PERMS_TTL_MS) return hit.access;

  const snap = await getDoc(doc(db, "tenants", unit.tenant, "units", unit.biz, "roles", role));
  const data = snap.exists() ? snap.data() : null;
  const access: RoleAccess = {
    perms: data?.permissions ?? data?.perms ?? [],
    allMenus: data?.allMenus === true,
  };
  permsCache.set(cacheKey, { access, at: now });
  return access;
}

async function loadOverrides(uid: string): Promise<PermOverrides> {
  const snap = await getDocs(collection(db, "users", uid, "perm_overrides"));
  const out: PermOverrides = {};
  snap.forEach((d) => {
    const e = d.data().effect;
    if (e === "allow" || e === "deny") out[d.id] = e;
  });
  return out;
}

function readClaims(rawClaims: Record<string, unknown>): AuthClaims {
  return {
    role: typeof rawClaims.role === "string" ? rawClaims.role : null,
    is_admin: rawClaims.is_admin === true,
    active: rawClaims.active !== false,
  };
}

async function buildUser(fbUser: FbUser): Promise<User> {
  const tokenResult = await fbUser.getIdTokenResult();
  const claims = readClaims(tokenResult.claims as Record<string, unknown>);
  const overrides = await loadOverrides(fbUser.uid);

  let name = fbUser.displayName ?? "";
  let loginId = "";
  let tenants: User["tenants"] = [];
  const userSnap = await getDoc(doc(db, "users", fbUser.uid));
  if (userSnap.exists()) {
    const u = userSnap.data();
    name = u.name ?? name;
    loginId = u.login_id ?? loginId;
    tenants = Array.isArray(u.tenants) ? (u.tenants as unknown[]).filter(isTenantKey) : [];
  }
  const { perms: permissions, allMenus } = await loadRoleAccess(claims.role, tenants);
  if (!loginId && fbUser.email?.endsWith(INTERNAL_EMAIL_DOMAIN)) {
    loginId = fbUser.email.slice(0, -INTERNAL_EMAIL_DOMAIN.length);
  }

  return {
    uid: fbUser.uid,
    loginId,
    name,
    email: fbUser.email ?? "",
    role: claims.role,
    isAdmin: claims.is_admin,
    active: claims.active,
    permissions,
    allMenus,
    overrides,
    tenants,
  };
}

/**
 * 로그인 상태 저장 방식(12 설계 §7-1).
 *   local   = 브라우저를 닫아도 유지(자동 로그인)
 *   session = 창(탭)을 닫으면 로그아웃 — 공용 PC 체크·자동 로그인을 꺼 둔 기기
 * 이미 로그인된 세션에도 즉시 적용된다(SDK auth.d.ts "This will affect the currently saved Auth session").
 */
export type AuthPersistenceKind = "local" | "session";
export async function applyPersistence(kind: AuthPersistenceKind): Promise<void> {
  await setPersistence(auth, kind === "session" ? browserSessionPersistence : browserLocalPersistence);
}

export interface LoginOptions {
  /** 저장 방식 — 로그인 전에 고른다. 기본 local */
  persistence?: AuthPersistenceKind;
}

/**
 * 로그인 — loginId + password.
 * 성공 시 도메인 User 반환. 비활성/미승인은 즉시 signOut 후 에러.
 * 저장 방식(setPersistence)은 signIn **앞**에 정한다(§7-1). 기기 확인 단계는 services/device_session.service.ts.
 */
export async function login(loginId: string, password: string, opts: LoginOptions = {}): Promise<User> {
  const email = loginId.trim() + INTERNAL_EMAIL_DOMAIN;
  try {
    await applyPersistence(opts.persistence ?? "local");
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const tokenResult = await cred.user.getIdTokenResult();
    const claims = readClaims(tokenResult.claims as Record<string, unknown>);
    if (!claims.active) {
      await signOut(auth);
      throw new AuthError("ACCOUNT_DISABLED");
    }
    if (!claims.role) {
      await signOut(auth);
      throw new AuthError("PENDING_APPROVAL");
    }
    return buildUser(cred.user);
  } catch (e) {
    if (e instanceof AuthError) throw e;
    const code = (e as { code?: string }).code;
    if (
      code === "auth/user-not-found" ||
      code === "auth/wrong-password" ||
      code === "auth/invalid-credential"
    ) {
      throw new AuthError("INVALID_CREDENTIALS");
    }
    if (code === "auth/too-many-requests") {
      throw new AuthError("TOO_MANY_ATTEMPTS");
    }
    throw new AuthError("UNKNOWN", (e as Error).message);
  }
}

/** 회원가입 입력값 — 아이디/비번/이름 + 인적정보. */
export interface SignupInput {
  /** 근무 법인(가입 화면에서 고름) — users.signup_tenant. 그 법인의 법인 관리자(또는 전체 admin)가 승인한다 */
  tenant: TenantKey;
  loginId: string;
  password: string;
  name: string;
  phone: string;
  personalEmail: string;
  birthDate: string;
  /** [필수] ERP 이용 규정 동의 — domain/consent.ts */
  termsConsent: boolean;
  /** [필수] 두 법인 계정 정보 공동 이용 동의 — domain/consent.ts */
  jointUseConsent: boolean;
  /** [필수] 개인정보 수집·이용 안내 확인 — domain/consent.ts */
  privacyNoticeConsent: boolean;
}

/**
 * 자율 회원가입 — Firebase Auth 계정을 직접 생성한다.
 *
 * 비밀번호는 Auth 로만 들어가고 Firestore 에는 평문 저장되지 않는다.
 * 생성 직후 본인 users/{uid} 문서를 role:"" active:false (승인 대기) 로 기록한 뒤
 * 즉시 signOut 한다. 관리자가 그룹(역할)을 배정해야 로그인 가능.
 *
 * onAuthUserCreate 트리거는 @rmc-internal.local 이메일을 early-return 하므로
 * 신규 계정엔 claims 가 없다 → login() 에서 role 없음 → PENDING_APPROVAL.
 */
export async function signup(input: SignupInput): Promise<void> {
  // 아이디는 소문자만 쓴다(가입 화면도 입력 즉시 소문자로 바꿈)
  const loginId = input.loginId.trim().toLowerCase();
  const email = loginId + INTERNAL_EMAIL_DOMAIN;
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, input.password);
    try {
      await updateProfile(cred.user, { displayName: input.name.trim() });
      await setDoc(doc(db, "users", cred.user.uid), {
        login_id: loginId,
        name: input.name.trim(),
        email: "",
        phone: input.phone.trim(),
        work_email: "",
        personal_email: input.personalEmail.trim(),
        birth_date: input.birthDate,
        consents: {
          ...(input.termsConsent
            ? { terms: { version: TERMS_CONSENT_VERSION, agreed_at: serverTimestamp() } }
            : {}),
          ...(input.jointUseConsent
            ? { joint_use: { version: JOINT_USE_CONSENT_VERSION, agreed_at: serverTimestamp() } }
            : {}),
          ...(input.privacyNoticeConsent
            ? { privacy_notice: { version: PRIVACY_NOTICE_VERSION, agreed_at: serverTimestamp() } }
            : {}),
        },
        role: "",
        active: false,
        // rules: 있으면 허용 법인만. 승인 함수(user.approve_signup)가 법인 관리자의 자기 법인 신청인지 이 값으로 판정
        signup_tenant: input.tenant,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
    } finally {
      // 승인 전까지 세션을 남기지 않는다.
      await signOut(auth);
    }
  } catch (e) {
    if (e instanceof AuthError) throw e;
    const code = (e as { code?: string }).code;
    if (code === "auth/email-already-in-use") {
      throw new AuthError("ID_TAKEN");
    }
    if (code === "auth/weak-password") {
      throw new AuthError("WEAK_PASSWORD");
    }
    throw new AuthError("UNKNOWN", (e as Error).message);
  }
}

/**
 * 로그아웃. log 를 주면 signOut **전에** 접속 기록을 남긴다(로그아웃 뒤에는 rules 가 쓰기를 막음).
 * 기록 실패는 무시(writeSelfAccessLog 가 삼킴).
 */
export async function logout(log?: SelfAccessLogInput | null): Promise<void> {
  permsCache.clear();
  if (log && auth.currentUser?.uid === log.uid) await writeSelfAccessLog(log);
  await signOut(auth);
}

/**
 * 본인 비밀번호 변경(D-1) — 현재 비밀번호로 재인증 → 새 비밀번호.
 * 다른 기기 로그아웃은 호출부가 account_ops self.password_changed 로 요청한다(§6-2).
 */
export async function changeMyPassword(currentPassword: string, nextPassword: string): Promise<void> {
  const fb = auth.currentUser;
  if (!fb || !fb.email) throw new AuthError("UNKNOWN", "로그인이 필요합니다.");
  try {
    await reauthenticateWithCredential(fb, EmailAuthProvider.credential(fb.email, currentPassword));
  } catch (e) {
    const code = (e as { code?: string }).code;
    if (code === "auth/wrong-password" || code === "auth/invalid-credential" || code === "auth/invalid-login-credentials") {
      throw new AuthError("WRONG_CURRENT_PASSWORD");
    }
    if (code === "auth/too-many-requests") throw new AuthError("TOO_MANY_ATTEMPTS");
    throw new AuthError("UNKNOWN", (e as Error).message);
  }
  try {
    await updatePassword(fb, nextPassword);
  } catch (e) {
    const code = (e as { code?: string }).code;
    if (code === "auth/weak-password") throw new AuthError("WEAK_PASSWORD");
    if (code === "auth/requires-recent-login") throw new AuthError("REQUIRES_RECENT_LOGIN");
    throw new AuthError("UNKNOWN", (e as Error).message);
  }
}

/**
 * onAuthStateChanged 구독. user는 로그인됐으면 도메인 User, 아니면 null.
 * active=false 계정은 자동 로그아웃되며 콜백은 null로 호출된다.
 *
 * unsubscribe 함수 반환.
 */
export function watchUser(cb: (user: User | null, error?: Error) => void): () => void {
  return onAuthStateChanged(auth, async (fbUser) => {
    if (!fbUser) {
      cb(null);
      return;
    }
    try {
      const user = await buildUser(fbUser);
      if (!user.active) {
        await signOut(auth);
        return;
      }
      cb(user);
    } catch (e) {
      cb(null, e as Error);
    }
  });
}
